import React, { useState } from 'react';
import useGateway from '../../hooks/useGateway';
import { DATE_PRESETS } from '../../lib/liveIntelOptions';
import { money, formatDate } from '../../lib/formatters';
import Pager from './Pager';

const Spin = () => (
  <div className="empty">
    <div className="spin" />
    <span>Loading…</span>
  </div>
);

const Fail = ({ error }) => (
  <div className="empty" style={{ color: 'var(--red)' }}>
    <b>Request failed</b>
    {error?.message || String(error)}
  </div>
);

const priceRange = (offer) => {
  const p = offer?.priceSpecification || {};
  if (p.minPrice != null && p.maxPrice != null && p.minPrice !== p.maxPrice) {
    return `${money(p.minPrice, p.priceCurrency)}–${money(p.maxPrice, p.priceCurrency)}`;
  }
  if (p.price != null) return money(p.price, p.priceCurrency);
  if (p.minPrice != null) return `from ${money(p.minPrice, p.priceCurrency)}`;
  return '';
};

export default function Streams() {
  const [draft, setDraft] = useState({ eventDatePreset: '', eventDateFrom: '', eventDateTo: '' });
  const [applied, setApplied] = useState({});
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(40);

  const { data, loading, error } = useGateway('lyveStreams', { ...applied, page, perPage });

  const submit = (e) => {
    e.preventDefault();
    setApplied(Object.fromEntries(Object.entries(draft).filter(([, v]) => v !== '')));
    setPage(1);
  };

  const streams = data?.streams || [];
  const totalItems = data?.pagination?.totalItems || streams.length;

  return (
    <div className="lyve-intel">
      <div className="hero" style={{ paddingBottom: 0 }}>
        <div className="eyebrow">
          <i />
          EXPLORE
        </div>
        <h1 className="h-title wide">LIVESTREAMS</h1>
        <p className="h-sub">Upcoming music livestreams, with broadcast-of-event links and ticket offers.</p>
      </div>

      <div className="page" style={{ paddingTop: 18 }}>
        <div className="notice">
          Livestream coverage is best-effort — JamBase indexes a limited set of webcasts, so many queries return few or
          no results.
        </div>

        <form className="acat-bar" onSubmit={submit}>
          <select
            className="acat-select"
            value={draft.eventDatePreset}
            onChange={(e) => setDraft({ ...draft, eventDatePreset: e.target.value })}>
            {DATE_PRESETS.map(([v, l]) => (
              <option key={v || 'any'} value={v}>
                {l}
              </option>
            ))}
          </select>
          <input
            className="fs-date"
            type="date"
            value={draft.eventDateFrom}
            onChange={(e) => setDraft({ ...draft, eventDateFrom: e.target.value })}
            aria-label="From date"
          />
          <input
            className="fs-date"
            type="date"
            value={draft.eventDateTo}
            onChange={(e) => setDraft({ ...draft, eventDateTo: e.target.value })}
            aria-label="To date"
          />
          <button className="acat-go" type="submit">
            Search
          </button>
        </form>

        {loading && <Spin />}
        {error && <Fail error={error} />}
        {!loading && !error && streams.length === 0 && (
          <div className="empty">
            <b>No livestreams found for this window.</b>
          </div>
        )}
        {!loading && !error && streams.length > 0 && (
          <>
            <div className="rp-grid rp-cols-2">
              {streams.map((s, i) => {
                const offers = s.offers || [];
                return (
                  <div className="geo-card" key={i}>
                    <div className="when">{formatDate(s.startDate)}</div>
                    <h3>{s.name}</h3>
                    <div className="sub" style={{ marginTop: 6 }}>
                      {s.performer?.map((p) => p.name).join(', ') || 'TBA'}
                    </div>
                    {s.broadcastOfEvent?.length > 0 && (
                      <div className="sub" style={{ color: 'var(--fg-4)', marginTop: 4 }}>
                        Broadcast of: {s.broadcastOfEvent.map((b) => b.name).join(', ')}
                      </div>
                    )}
                    {offers.map((o, j) => (
                      <a className="offer" href={o.url} target="_blank" rel="noreferrer" key={j}>
                        <div>
                          <div className="seller">{o.seller?.name || o.name}</div>
                        </div>
                        <div className="price">{priceRange(o)}</div>
                      </a>
                    ))}
                  </div>
                );
              })}
            </div>
            <Pager
              total={totalItems}
              page={page}
              perPage={perPage}
              onPage={setPage}
              onPerPage={(v) => {
                setPerPage(v);
                setPage(1);
              }}
            />
          </>
        )}
      </div>
    </div>
  );
}
